/**
 * Hybrid Undo/Redo system.
 *
 * All operations use instant inverse commands — no full reload:
 * - Move: sendUpdateInstancePosition to original position
 * - Connect: sendDisconnectInstance
 * - Disconnect: sendConnectModules with saved params
 * - Delete: sendAddModule with same instance_id (reuse ID so children reconnect)
 *           then reconnect children
 *
 * The instance_id reuse is key: when a deleted parent is re-added with its
 * original ID, children's parent_instance_id references still point to it.
 */

import type { InstanceData } from "../types";
import { useViewerStore } from "../store";

// --- Action types ---

interface MoveAction {
  kind: "move";
  instanceId: string;
  position: [number, number, number];
}

interface ConnectAction {
  kind: "connect";
  childInstanceId: string;
}

/** Combined: disconnect + move back (for drag-to-connect undo) */
interface ConnectWithMoveAction {
  kind: "connect_with_move";
  childInstanceId: string;
  position: [number, number, number];
}

interface DisconnectAction {
  kind: "disconnect";
  childInstanceId: string;
  childLinkName: string;
  parentInstanceId: string;
  parentLinkName: string;
}

interface DeleteAction {
  kind: "delete";
  instanceId: string;
  moduleId: string;
  position: [number, number, number];
  // Children that were connected to this instance
  children: Array<{
    childInstanceId: string;
    childLinkName: string;
    parentLinkName: string;
  }>;
}

interface JointAction {
  kind: "joint";
  jointName: string;
  value: number;
}

/** Batch restore of multiple joint angles (for IK undo) */
interface JointBatchAction {
  kind: "joint_batch";
  joints: Array<{ name: string; value: number }>;
}

type UndoAction = MoveAction | ConnectAction | ConnectWithMoveAction | DisconnectAction | DeleteAction | JointAction | JointBatchAction;

interface UndoEntry {
  label: string;
  action: UndoAction;
}

// --- Senders ---

interface UndoSenders {
  sendAddModule: (moduleId: string, position: [number, number, number], instanceId?: string) => void;
  sendDeleteInstance: (instanceId: string) => void;
  sendUpdateInstancePosition: (instanceId: string, position: [number, number, number]) => void;
  sendTranslationEnd: () => void;
  sendConnectModules: (
    childInstanceId: string,
    childLinkName: string,
    parentInstanceId: string,
    parentLinkName: string,
    yaw?: number,
    pitch?: number,
    roll?: number,
    offset?: [number, number, number],
  ) => void;
  sendDisconnectInstance: (instanceId: string) => void;
  sendJointSliderUpdate: (jointName: string, value: number) => void;
}

const MAX_HISTORY = 50;
const undoStack: UndoEntry[] = [];
const redoStack: UndoEntry[] = [];
let _senders: UndoSenders | null = null;

export function initUndoSystem(senders: UndoSenders) {
  _senders = senders;
}

function push(entry: UndoEntry) {
  undoStack.push(entry);
  if (undoStack.length > MAX_HISTORY) undoStack.shift();
  redoStack.length = 0;
}

// --- Record functions ---

/** Record position before dragging. */
export function recordMove(instanceId: string, position: [number, number, number]) {
  push({ label: "Move", action: { kind: "move", instanceId, position: [...position] } });
}

/** Record a connect. Merges with preceding move for same instance. */
export function recordConnect(childInstanceId: string) {
  const last = undoStack[undoStack.length - 1];
  if (last && last.action.kind === "move" && last.action.instanceId === childInstanceId) {
    last.label = "Connect";
    last.action = {
      kind: "connect_with_move",
      childInstanceId,
      position: (last.action as MoveAction).position,
    };
    redoStack.length = 0;
    return;
  }
  push({ label: "Connect", action: { kind: "connect", childInstanceId } });
}

/** Record a disconnect with connection params. */
export function recordDisconnect(
  childInstanceId: string,
  childLinkName: string,
  parentInstanceId: string,
  parentLinkName: string,
) {
  push({
    label: "Disconnect",
    action: { kind: "disconnect", childInstanceId, childLinkName, parentInstanceId, parentLinkName },
  });
}

/** Record a delete. Captures children so they can be reconnected on undo. */
export function recordDelete(instance: InstanceData, allInstances: InstanceData[]) {
  const children = allInstances
    .filter((i) => i.parent_instance_id === instance.instance_id)
    .map((child) => ({
      childInstanceId: child.instance_id,
      childLinkName: child.child_link_name || "",
      parentLinkName: child.parent_link_name || "",
    }));

  push({
    label: "Delete",
    action: {
      kind: "delete",
      instanceId: instance.instance_id,
      moduleId: instance.module_name,
      position: [...instance.position],
      children,
    },
  });
}

/** Record a single joint angle change. Pass the CURRENT value before the change. */
export function recordJoint(jointName: string, currentValue: number) {
  push({ label: "Joint angle", action: { kind: "joint", jointName, value: currentValue } });
}

/** Record all joint angles at once (call before IK drag starts). */
export function recordJointBatch(joints: Array<{ name: string; value: number }>) {
  push({ label: "IK", action: { kind: "joint_batch", joints: joints.map((j) => ({ ...j })) } });
}

// --- Execute ---

function execute(action: UndoAction): void {
  if (!_senders) return;

  switch (action.kind) {
    case "move":
      _senders.sendUpdateInstancePosition(action.instanceId, action.position);
      _senders.sendTranslationEnd();
      break;
    case "connect":
      _senders.sendDisconnectInstance(action.childInstanceId);
      break;
    case "connect_with_move":
      _senders.sendDisconnectInstance(action.childInstanceId);
      _senders.sendUpdateInstancePosition(action.childInstanceId, action.position);
      _senders.sendTranslationEnd();
      break;
    case "disconnect":
      _senders.sendConnectModules(
        action.childInstanceId,
        action.childLinkName,
        action.parentInstanceId,
        action.parentLinkName,
      );
      break;
    case "joint":
      _senders.sendJointSliderUpdate(action.jointName, action.value);
      break;
    case "joint_batch":
      for (const j of action.joints) {
        _senders.sendJointSliderUpdate(j.name, j.value);
      }
      break;
    case "delete": {
      _senders.sendAddModule(action.moduleId, action.position, action.instanceId);
      if (action.children.length > 0) {
        const senders = _senders;
        const parentId = action.instanceId;
        setTimeout(() => {
          for (const child of action.children) {
            senders.sendConnectModules(
              child.childInstanceId,
              child.childLinkName,
              parentId,
              child.parentLinkName,
            );
          }
        }, 300);
      }
      break;
    }
  }
}

/**
 * Build a redo action by capturing the current state for the same kind of operation.
 */
function captureCurrentState(action: UndoAction): UndoAction | null {
  const state = useViewerStore.getState();

  switch (action.kind) {
    case "move": {
      const inst = state.instances.find((i) => i.instance_id === action.instanceId);
      if (inst) {
        return { kind: "move", instanceId: action.instanceId, position: [...inst.position] };
      }
      return null;
    }
    case "connect": {
      // Current state: connected. Redo = reconnect (but we'd need link names).
      // Store the connection info from current instances.
      const inst = state.instances.find((i) => i.instance_id === action.childInstanceId);
      if (inst && inst.parent_instance_id && inst.parent_link_name && inst.child_link_name) {
        return {
          kind: "disconnect",
          childInstanceId: inst.instance_id,
          childLinkName: inst.child_link_name,
          parentInstanceId: inst.parent_instance_id,
          parentLinkName: inst.parent_link_name,
        };
      }
      return null;
    }
    case "connect_with_move": {
      const inst = state.instances.find((i) => i.instance_id === action.childInstanceId);
      if (inst && inst.parent_instance_id && inst.parent_link_name && inst.child_link_name) {
        return {
          kind: "disconnect",
          childInstanceId: inst.instance_id,
          childLinkName: inst.child_link_name,
          parentInstanceId: inst.parent_instance_id,
          parentLinkName: inst.parent_link_name,
        };
      }
      return null;
    }
    case "disconnect": {
      return { kind: "connect", childInstanceId: action.childInstanceId };
    }
    case "delete": {
      // Current state: instance exists. Redo = delete it again.
      const inst = state.instances.find((i) => i.instance_id === action.instanceId);
      if (inst) {
        const children = state.instances
          .filter((i) => i.parent_instance_id === action.instanceId)
          .map((child) => ({
            childInstanceId: child.instance_id,
            childLinkName: child.child_link_name || "",
            parentLinkName: child.parent_link_name || "",
          }));
        return {
          kind: "delete",
          instanceId: inst.instance_id,
          moduleId: inst.module_name,
          position: [...inst.position],
          children,
        };
      }
      return null;
    }
    case "joint": {
      const joint = state.joints.find((j) => j.name === action.jointName);
      if (joint) {
        return { kind: "joint", jointName: action.jointName, value: joint.current_angle };
      }
      return null;
    }
    case "joint_batch": {
      const currentJoints = state.joints.map((j) => ({ name: j.name, value: j.current_angle }));
      return { kind: "joint_batch", joints: currentJoints };
    }
  }
}

export function undo(): boolean {
  const entry = undoStack.pop();
  if (!entry) return false;

  // Capture current state as redo BEFORE executing undo
  const redoAction = captureCurrentState(entry.action);
  execute(entry.action);
  if (redoAction) {
    redoStack.push({ label: entry.label, action: redoAction });
  }
  return true;
}

export function redo(): boolean {
  const entry = redoStack.pop();
  if (!entry) return false;

  // Capture current state as undo BEFORE executing redo
  const undoAction = captureCurrentState(entry.action);
  execute(entry.action);
  if (undoAction) {
    undoStack.push({ label: entry.label, action: undoAction });
  }
  return true;
}
